import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useGetOrderQuery } from '@printdeal/checkout-frontend';
import { ErrorManagement } from '@printdeal/error-management/react';
import { AnonymousTokenNotAllowedError } from '../../../../errors/AnonymousTokenNotAllowedError';
import { GetOrdersParams } from '../../../../services/order/OrderService';
import { RootState } from '../../../../store';
// @ts-expect-error
import { selectors as customerSelectors } from '../../../../store/slices/customerSlice';
import LineItem from '../../../../types/cart/LineItem';
import { ServiceFactory } from '../../../../services/ServiceFactory';
import { sanitizeOrder } from '../../helpers/order-helper';
import {
  dispatchAccountEvent,
  DispatchCategoriesAccount,
} from '../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import { OrderWithTotalDetails, SanitizedOrder } from '../../../../types/order/Order';

/**
 *  Hook for fetching orders
 */
export const useOrders = (params: GetOrdersParams) => {
  const customerId = useSelector<RootState, string | undefined>((state) => customerSelectors.getCustomerId(state));

  const { data, status, isRefetching } = useQuery({
    queryKey: ['orders', { ...params, customerId }],
    queryFn: () => ServiceFactory.createOrderService().getOrders(params),
  });

  return {
    total: data?.total,
    orders: data?.results,
    status,
    isRefetching,
  };
};

/**
 * Hook for validating order has valid quote line items for reordering quote lineItems
 * @returns
 */
export const useOrderQuotesValidation = (lineItems: Array<LineItem>) => {
  const quoteIds: string[] = [];
  lineItems.forEach((lineItem) => {
    const quoteId = lineItem.custom?.fields?.quoteId;
    if (quoteId && !quoteIds.includes(quoteId)) {
      quoteIds.push(quoteId);
    }
  });
  if (quoteIds.length > 0) {
    const quoteIdstring: string = quoteIds.join(',');
    try {
      const { data } = useQuery({
        queryKey: ['quote', quoteIdstring],
        queryFn: () => ServiceFactory.createQuotesService().validateQuote(quoteIdstring),
      });
      return data;
    } catch (e) {
      return [];
    }
  }
  return [];
};

export const useSanitizedOrder = (
  orderId: string,
  locale: string,
  gaEventCategoryOnFailure: DispatchCategoriesAccount,
  forReOrder: boolean = false,
): {
  order: SanitizedOrder | undefined;
  isSuccess: boolean;
  error: unknown;
  isError: boolean;
} => {
  const {
    data: order, isSuccess, error, isError,
  } = useGetOrderQuery({ orderId });

  if (isSuccess && (order?.message || order)) {
    return {
      order: sanitizeOrder(order?.message as OrderWithTotalDetails || order, locale, forReOrder),
      error,
      isError,
      isSuccess,
    };
  }

  if (isError) {
    const isAnonymousTokenError = error instanceof AnonymousTokenNotAllowedError;
    const isNotFoundError = Boolean(
      error instanceof AxiosError
      && error.response?.status
      && [400, 404].includes(error.response.status),
    );

    if (!isAnonymousTokenError && !isNotFoundError) {
      ErrorManagement.exception({
        exception: error,
        extra: { orderId, locale },
        tags: {
          fileLocation: 'useOrder',
          featureFunction: 'useSanitizedOrder',
        },
        fingerprint: ['{{default}}', 'hooks-use-sanitized-order'],
      });
    }
    dispatchAccountEvent(gaEventCategoryOnFailure, 'error_get_order');
    return {
      error,
      isError,
      order: undefined,
      isSuccess,
    };
  }

  return {
    error,
    isError,
    order,
    isSuccess,
  };
};
